import { useEffect } from 'react';

type OpenPanelGlobal = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
    interface Window {
        op?: OpenPanelGlobal;
    }
}

export interface OpenPanelLoaderProps {
    /**
     * Kill switch for the trial: unset (or falsy) disables the snippet entirely
     * with no code change, so the trial can be paused by config alone.
     */
    enabled?: boolean | string;
    /** The OpenPanel project's client id. Required for the snippet to send events. */
    clientId?: string;
    /** Base URL of the self-hosted OpenPanel API, when not the default OpenPanel Cloud. */
    apiUrl?: string;
    /** Override for the tracker script URL, when not the default OpenPanel Cloud script. */
    scriptUrl?: string;
}

const DEFAULT_SCRIPT_URL = 'https://openpanel.dev/op1.js';

// The OpenPanel trial's real, live values. The client id is write-only and
// safe to embed in public JS. Exported so a surface with no per-app config
// plumbing of its own (e.g. a fragment gated by a single enabled flag) can
// still point at the same live project without duplicating these literals.
export const OPENPANEL_TRIAL_CLIENT_ID = '191aa02e-54c7-4acb-83c7-9aa14c077f1f';
export const OPENPANEL_TRIAL_API_URL = 'https://api.open-panel.es.vpsvc.com';
export const OPENPANEL_TRIAL_SCRIPT_URL = 'https://static.open-panel.es.vpsvc.com/op1.js';

// Ports OpenPanel's own documented bootstrap (https://openpanel.dev/docs/sdks/script)
// to TypeScript: a Proxy-backed queue that buffers calls made before the real
// script has loaded, so `window.op('init', ...)` below is safe to call
// immediately.
function createOpenPanelQueue(): OpenPanelGlobal {
    const queue: unknown[][] = [];
    const target = ((...args: unknown[]) => {
        if (args.length) {
            queue.push(args);
        }
    }) as OpenPanelGlobal;

    return new Proxy(target, {
        get(_target, prop) {
            if (prop === 'q') {
                return queue;
            }
            return (...args: unknown[]) => {
                queue.push([prop as string, ...args]);
            };
        },
        has(_target, prop) {
            return prop === 'q';
        },
    });
}

function OpenPanelLoader({ enabled, clientId, apiUrl, scriptUrl }: OpenPanelLoaderProps) {
    useEffect(() => {
        if (!enabled || !clientId) {
            return;
        }

        if (document.getElementById('openpanel-script')) {
            return;
        }

        window.op = window.op || createOpenPanelQueue();
        window.op('init', {
            clientId,
            apiUrl,
            trackScreenViews: true,
            trackOutgoingLinks: true,
            trackAttributes: true,
            // Loaded asynchronously as a separate script by OpenPanel itself
            // only for recorded sessions, so this doesn't add weight to op1.js
            // when disabled. Left at OpenPanel's own privacy-safe defaults
            // (maskAllText/maskAllInputs both true) — nothing here is
            // sensitive-content-aware enough to safely narrow those.
            sessionReplay: {
                enabled: true,
                // The default 1MB silently drops the initial rrweb
                // FullSnapshot for a page whose serialized snapshot exceeds
                // it — no error, just an unplayable recording. Raised just
                // under the API's 2MB hard cap rather than all the way to it,
                // to leave headroom for a snapshot larger than what's been
                // measured so far.
                maxPayloadBytes: 1_900_000,
                // Keep production replay volume bounded after e2e validation:
                // record one in ten eligible sessions.
                sampleRate: 0.1,
            },
        });

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.defer = true;
        script.id = 'openpanel-script';
        script.setAttribute('data-testid', 'openpanel-script');
        script.src = scriptUrl || DEFAULT_SCRIPT_URL;

        document.body.appendChild(script);
    }, [enabled, clientId, apiUrl, scriptUrl]);

    return null;
}

export { OpenPanelLoader };
